/**
 * Utilities for parsing DynamoDB-marshalled JSON strings into plain JS values.
 *
 * Some fields in ChatbotChatHistory (rooms_shown, booking_result,
 * hotel_query_analytics, booking_summary) are stored as DynamoDB String
 * attributes whose value is itself a JSON string containing DynamoDB type
 * descriptors ({S:"…"}, {N:"…"}, {M:{…}}, {L:[…]}, etc.).
 *
 * The backend returns them as-is, so the frontend must parse and unmarshal
 * them before use.
 */

/**
 * Recursively unmarshal a single DynamoDB-typed value into a plain JS value.
 *
 * Supported type descriptors: S, N, BOOL, NULL, L, M, SS, NS.
 */
const unmarshalValue = (value) => {
  if (value === null || value === undefined) return value;
  if (typeof value !== "object") return value;

  const keys = Object.keys(value);

  if (keys.length === 1) {
    const type = keys[0];
    const inner = value[type];

    switch (type) {
      case "S":
        return inner;
      case "N":
        return Number(inner);
      case "BOOL":
        return inner;
      case "NULL":
        return null;
      case "L":
        return Array.isArray(inner) ? inner.map(unmarshalValue) : [];
      case "M": {
        const result = {};
        for (const [k, v] of Object.entries(inner)) {
          result[k] = unmarshalValue(v);
        }
        return result;
      }
      case "SS":
        return Array.isArray(inner) ? inner : [];
      case "NS":
        return Array.isArray(inner) ? inner.map(Number) : [];
      default:
        break;
    }
  }

  if (Array.isArray(value)) {
    return value.map(unmarshalValue);
  }

  const result = {};
  for (const [k, v] of Object.entries(value)) {
    result[k] = unmarshalValue(v);
  }
  return result;
};

/**
 * Try to JSON-parse a string and unmarshal any DynamoDB type descriptors.
 * Returns the original value unchanged when:
 *  - input is not a non-empty string, OR
 *  - JSON.parse fails (i.e. it's just a plain text value).
 */
export const parseDynamoField = (value) => {
  if (!value || typeof value !== "string") return value;
  try {
    const parsed = JSON.parse(value);
    return unmarshalValue(parsed);
  } catch {
    return value;
  }
};
